import { loginInputSchema } from '@ai-sales/shared'
import { useState, type FormEvent } from 'react'
import { Navigate, useLocation } from 'react-router'
import { FullScreenLoader } from '../auth/require-auth.js'
import { useLogin, useSession } from '../auth/session-context.js'
import { Button } from '../components/button.js'
import { ErrorPanel } from '../components/error-panel.js'
import { Field } from '../components/field.js'

interface LocationState {
  from?: string
}

export function LoginPage() {
  const { status } = useSession()
  const location = useLocation()
  const login = useLogin()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  if (status === 'loading') {
    return <FullScreenLoader />
  }

  if (status === 'authenticated') {
    // Возвращаем туда, куда человек шёл до перехода на вход
    const from = (location.state as LocationState | null)?.from
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()

    /**
     * Проверка той же схемой, которой сервер проверит запрос. Смысл не в
     * экономии запроса, а в том, что сообщение об ошибке формата будет
     * одинаковым здесь и там — расхождение правил между формой и сервером
     * невозможно по построению.
     */
    const parsed = loginInputSchema.safeParse({ email, password })

    if (!parsed.success) {
      setFormError('Проверьте адрес и пароль: адрес должен быть похож на почтовый.')
      return
    }

    setFormError(null)
    login.mutate(parsed.data)
  }

  return (
    <div className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="mb-6">
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className="grid size-7 shrink-0 place-items-center rounded-md border border-accent font-mono text-[11px] font-semibold text-accent"
            >
              LP
            </span>
            <span className="text-[13px] font-semibold tracking-tight text-ink">LeadPilot</span>
          </div>
          <h1 className="mt-3 text-xl font-semibold text-ink">Вход</h1>
          <p className="mt-1 text-sm text-muted">
            Регистрации снаружи нет: доступ выдаёт владелец воркспейса.
          </p>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field
            label="Рабочий адрес"
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />

          <Field
            label="Пароль"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={formError}
          />

          {login.error && <ErrorPanel error={login.error} />}

          <Button type="submit" variant="primary" loading={login.isPending} className="w-full">
            Войти
          </Button>
        </form>

        <p className="mt-6 text-xs text-muted">
          Первый владелец заводится отдельно: на своём компьютере —{' '}
          <code className="font-mono">Создать-владельца.cmd</code> в папке проекта, на сервере —{' '}
          <code className="font-mono">pnpm --filter @ai-sales/api bootstrap</code>.
        </p>
      </div>
    </div>
  )
}
